import ReactDOM from 'react-dom';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ReactComponent as MoneyIcon } from 'assets/icons/icon-map-bank.svg';
import { ReactComponent as GoBackArrowIcon } from 'assets/icons/go-back-arrow.svg';

import { useOutside } from 'hooks/UseOutside';

import {
  Container,
  ModalCloseButton,
  ModalContainer,
  ModalContent,
} from './_withdrawModal';

import { SelectWithdrawOption } from './components/SelectWithdrawOption/SelectWithdrawOption';
import { SetWithdrawValue } from './components/SetWithdrawValue/SetWithdrawValue';
import { ConfirmWithdrawAccount } from './components/ConfirmWithdrawAccount/ConfirmWithdrawAccount';
import { toast } from 'react-toastify';
import api from 'services/api';
import { IBankAccount } from './interfaces';

interface IWithdrawModalProps {
  toggleModal: Dispatch<SetStateAction<boolean>>;
  callback?: () => void;
}

const WithdrawalOptions = [
  {
    icon: MoneyIcon,
    title: 'Conta',
    subtitle: 'creditado em até 2 dias úteis',
  },
];

export const WithdrawModal = ({ toggleModal }: IWithdrawModalProps) => {
  const modalRoot = document.getElementById('root-modal');
  const modalRef = useRef(null);

  const [hideModal, setHideModal] = useState(false);

  const [bankAccountData, setBankAccountData] = useState<IBankAccount>();

  const [value, setValue] = useState('');
  const [optionTitle, setOptionTitle] = useState('');
  const [withdrawAllStep, setWithdrawAllStep] = useState(1);
  const [wasSuccessful, setWasSuccessful] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [gains, setGains] = useState('');
  const [toReceive, setToReceive] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const ensureBankAccountData = async () => {
    try {
      const response = await api.get<{
        results: IBankAccount[];
      }>('/banks');

      setBankAccountData(response.data?.results[0]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Ocorreu um erro ao obter dados bancários, tente novamente mais tarde',
      );
    }
  };

  useEffect(() => {
    ensureBankAccountData();
  }, []);

  const handleValueChange = (newValue: string) => {
    const newValueAsNumber = parseInt(newValue);
    setValue(newValue);
    setIsButtonDisabled(newValueAsNumber < 10);
  };
  
  const handleCloseModal = () => {
    setHideModal((prevState) => !prevState);
    setValue('');
    setOptionTitle('');
    setWithdrawAllStep(1);
    setWasSuccessful(false);
    setHasError(false);

    setTimeout(() => {
      toggleModal((prevState) => !prevState);
    }, 500);
  };

  useOutside(modalRef, handleCloseModal);

  if (!modalRoot) {
    return null;
  }

  const selectedOption = WithdrawalOptions.find(
    (option) => option.title === optionTitle,
  );

  const withdrawAllSteps = !!selectedOption
    ? {
        1: <SetWithdrawValue value={value} onValueChange={handleValueChange} gains={gains} toReceive={toReceive} />,
        2: (
          <ConfirmWithdrawAccount
            icon={selectedOption.icon}
            title={selectedOption.title}
            value={value}
            bankAccountData={bankAccountData}
            wasSuccessful={wasSuccessful}
            hasError={hasError}
          />
        ),
      }
    : ({} as { [key: number]: JSX.Element });

  const requestWithdrawal = async () => {
    try {
      const response = await api.post('/providers/transfer', {
        amount: (Number(value) * 100).toString(),
      })

      setWasSuccessful(true);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Ocorreu um erro ao sacar o dinheiro, tente novamente mais tarde',
      );
      setHasError(true);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  
    if (parseInt(value) < 10) {
      toast.error('O valor mínimo para saque é de R$10,00');
      return;
    }
  
    if (withdrawAllStep < Object.keys(withdrawAllSteps).length) {
      setWithdrawAllStep((prevState) => prevState + 1);
    } else {
      requestWithdrawal();
    }
  };
  
  const handleGoBack = () => {
    if (withdrawAllStep > 1) {
      setWithdrawAllStep((prevState) => prevState - 1);
    } else {
      setOptionTitle('');
    }
  };

  const handleOptionChange = (newOptionTitle: string) => {
    if (bankAccountData) {
      setOptionTitle(newOptionTitle);
    } else {
      toast.error(
        'Por favor cadastre seus dados bancários antes de realizar esta operação!',
      );
    }
  }

  return ReactDOM.createPortal(
    <Container className={`${hideModal ? 'modal-withdraw--hide' : ''}`}>
      <ModalContainer ref={modalRef}>
        <ModalCloseButton type="button" onClick={handleCloseModal}>
          X
        </ModalCloseButton>
        <ModalContent as="form" onSubmit={handleSubmit}>
          {selectedOption ? (
            <>
              <header className="modal-withdraw__selected-header">
                {<selectedOption.icon className="modal-withdraw__icon" />}
                <h1 className="modal-withdraw__title">
                  {selectedOption.title}
                </h1>
              </header>
              {withdrawAllSteps[withdrawAllStep]}
              {!(wasSuccessful || hasError) && (
                <>
                  <button
                    type="submit"
                    className="modal-withdraw__submit-button"
                  >
                    Continuar
                  </button>
                  <button
                    onClick={handleGoBack}
                    type="button"
                    className="modal-withdraw__go-back-button"
                  >
                    <GoBackArrowIcon />
                  </button>
                </>
              )}
            </>
          ) : (
            <SelectWithdrawOption
              options={WithdrawalOptions}
              onOptionClick={handleOptionChange}
            />
          )}
        </ModalContent>
      </ModalContainer>
    </Container>,
    modalRoot,
  );
};
